import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { Modal } from '@components/common/modal/modal';
import { useBirthdayQuery } from '@services/hooks/dashboard/useBirthdayQuery';
import { Link } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { ToastType, useTosterStore } from '@store/toster';
import { overlayRemoveFadeAction } from '@components/common/sort';
import { useBDEOnLeaveStore } from '@store/bdeOnLeaveStore';
import { useCommonModalPopUpStore } from '@store/useCommonModalPopUpStore';
import { NoDataFound } from '@components/common/no-data-found/noDataFound';
import { BirthdayItem } from '.';
import { useComponentLevelAccessCheck } from '../../../../authGuard/hooks/useComponentLevelAccessCheck';

export const BirthdayPopup = (): ReactElement => {
	const checkPermissions = useComponentLevelAccessCheck('dashboard-birthday-popup');
	const { setKeyBaseModalState, isBirthDayModalOpen: modalOpen } = useCommonModalPopUpStore();
	const { data, refetch } = useBirthdayQuery();
	const { setToaster } = useTosterStore();
	const { isModalOpen: bdeIsOnLeave } = useBDEOnLeaveStore();

	useEffect(() => {
		if (bdeIsOnLeave) {
			return;
		}
		const cookies = new Cookies();
		const lastLoginDate = cookies.get('lastLoginDate');
		const currentDate = new Date();
		const tomorrow = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
		tomorrow.setHours(0, 0, 0, 0);

		if (checkPermissions && (modalOpen || !lastLoginDate)) {
			refetch?.()
				.then((response) => {
					if (response?.data && response?.data?.data?.length > 0) {
						setKeyBaseModalState('isBirthDayModalOpen', true);
					}

					cookies.set('lastLoginDate', currentDate, {
						expires: tomorrow,
						path: '/',
						maxAge: 24 * 60 * 60,
					});
				})
				.catch((error) => {
					setToaster(true, ToastType.error, error);
				});
		}
	}, [checkPermissions, bdeIsOnLeave, modalOpen, refetch, setKeyBaseModalState, setToaster]);

	const birthdayList = useMemo<BirthdayItem[]>(() => data?.data || [], [data?.data]);

	const { todayBirthdays, tomorrowBirthdays } = useMemo(() => {
		const today = new Date();
		const tomorrow = new Date();
		tomorrow.setDate(today.getDate() + 1);

		const isSameDayAndMonth = (dateOfBirth: string, compareDate: Date): boolean => {
			const birthDate = new Date(dateOfBirth);

			return birthDate.getDate() === compareDate.getDate() && birthDate.getMonth() === compareDate.getMonth();
		};

		return {
			todayBirthdays: birthdayList.filter((item: BirthdayItem) => isSameDayAndMonth(item.dateofbirth, today)),
			tomorrowBirthdays: birthdayList.filter((item: BirthdayItem) => isSameDayAndMonth(item.dateofbirth, tomorrow)),
		};
	}, [birthdayList]);

	const handleModalClose = useCallback(
		(): void => overlayRemoveFadeAction(() => setKeyBaseModalState('isBirthDayModalOpen', false)),
		[setKeyBaseModalState]
	);

	const renderBirthdayList = (list: BirthdayItem[], startIndex = 0): ReactElement => {
		if (!list || list.length === 0) {
			return <p className="birthday-empty">No birthdays found</p>;
		}

		return (
			<ul className="birthday-box">
				{list.map((item: BirthdayItem, index: number) => (
					<li key={`${item.name}-${item.phone}`}>
						<div className="birthday-list-wrapper">
							<p className="h4 birthday-name">
								<span>{startIndex + index + 1}. </span>
								{item.name}
							</p>

							<div className="birthday-info">
								<Link to={'tel:+91' + item.phone} aria-label="call user" className="call">
									<span className="icon-call"></span>
								</Link>
							</div>
						</div>
					</li>
				))}
			</ul>
		);
	};

	if (!checkPermissions) {
		return <></>;
	}

	return (
		<Modal
			modalTitle={`Birthdays`}
			modalId="popupDialog"
			className="dialog-sm birthday-dialog"
			modalOpen={modalOpen}
			onModalClose={handleModalClose}
		>
			<div className="birthday-popup">
				{birthdayList.length > 0 ? (
					<>
						<div className="birthday-section">
							<h5 className="birthday-section-title">Today’s Birthday</h5>
							{renderBirthdayList(todayBirthdays)}
						</div>

						<div className="birthday-section">
							<h5 className="birthday-section-title">Tomorrow’s Birthday</h5>
							{renderBirthdayList(tomorrowBirthdays)}
						</div>
					</>
				) : (
					<NoDataFound className="text-center" />
				)}
			</div>
		</Modal>
	);
};
